import { Question, Answer, Result, Insight } from '../types';

export const calculateResults = (
  answers: Record<number, Answer>,
  questions: Question[]
): Result => {
  // Calculate score based on answers and question weights
  let score = 0;
  let maxScore = 0;
  
  questions.forEach(question => {
    if (question.weight && question.weight > 0) {
      maxScore += question.weight * 4;
      
      const answer = answers[question.id];
      if (question.type === 'single' && answer) {
        const option = question.options.find(opt => opt.id === answer);
        if (option && option.value) {
          score += option.value * question.weight;
        }
      } else if (question.type === 'multiple' && answer && Array.isArray(answer)) {
        const selectedCount = answer.length;
        const totalOptions = question.options.length;
        score += (selectedCount / totalOptions) * 4 * question.weight;
      }
    }
  });
  
  const finalScore = Math.round((score / maxScore) * 100);
  
  // Determine category based on score
  let category, categoryClass, categoryDescription;
  if (finalScore <= 25) {
    category = 'BAIXO';
    categoryClass = 'category-low';
    categoryDescription = 'Sua operação está relativamente otimizada';
  } else if (finalScore <= 50) {
    category = 'MÉDIO';
    categoryClass = 'category-medium';
    categoryDescription = 'Há oportunidades significativas de melhoria';
  } else if (finalScore <= 75) {
    category = 'ALTO';
    categoryClass = 'category-high';
    categoryDescription = 'Sua operação tem grande potencial de otimização';
  } else {
    category = 'CRÍTICO';
    categoryClass = 'category-critical';
    categoryDescription = 'Situação crítica requer atenção imediata';
  }
  
  // Calculate current state metrics
  const invoiceVolumeAnswer = answers[2] as string;
  let dailyHours = 2;
  let weeklyHours = 10;
  
  if (invoiceVolumeAnswer === 'up_to_100') {
    dailyHours = 1;
    weeklyHours = 5;
  } else if (invoiceVolumeAnswer === '100_to_500') {
    dailyHours = 3;
    weeklyHours = 15;
  } else if (invoiceVolumeAnswer === '500_to_1000') {
    dailyHours = 5;
    weeklyHours = 25;
  } else if (invoiceVolumeAnswer === '1000_to_5000') {
    dailyHours = 7;
    weeklyHours = 35;
  } else if (invoiceVolumeAnswer === 'more_than_5000') {
    dailyHours = 9;
    weeklyHours = 45;
  }
  
  // Error frequency text
  const errorFreqAnswer = answers[3] as string;
  let errorFreqText = 'Ocasionalmente';
  if (errorFreqAnswer === 'rare') errorFreqText = 'Raramente';
  else if (errorFreqAnswer === 'occasional') errorFreqText = 'Semanalmente';
  else if (errorFreqAnswer === 'frequent') errorFreqText = 'Várias vezes por semana';
  else if (errorFreqAnswer === 'daily') errorFreqText = 'Diariamente';
  
  // Calculate monthly cost (assuming R$50/hour total cost)
  const monthlyCost = weeklyHours * 4 * 50;
  
  // Calculate automated metrics (assuming 80% reduction)
  const automatedHours = Math.round(weeklyHours * 0.2);
  
  // Savings calculations
  const hourlyRate = 50; // R$50/hour (total costs)
  const timeSavings = weeklyHours - automatedHours;
  const monthlySavings = Math.round(timeSavings * 4 * hourlyRate);
  const annualSavings = monthlySavings * 12;
  const roiMonths = Math.max(1, Math.round(15000 / monthlySavings)); // Assuming R$15k investment
  
  // Generate insights
  const insights: Insight[] = [];
  
  // Manual hours insight
  if (invoiceVolumeAnswer === 'more_than_5000') {
    insights.push({
      type: 'alert',
      title: 'Alto volume de notas fiscais',
      message: 'O volume elevado de notas aumenta riscos de erros e atrasos',
      impact: 'Automação pode reduzir tempo de processamento em 85%'
    });
  }
  
  // Systems insight
  if (answers[4] === 'more_6') {
    insights.push({
      type: 'opportunity',
      title: 'Fragmentação de sistemas',
      message: 'Trabalhar com muitos sistemas aumenta erros em 40%',
      impact: 'Integração pode economizar 3h/dia'
    });
  }
  
  // Prefeituras insight
  if (answers[5] === 'more_30') {
    insights.push({
      type: 'alert',
      title: 'Alta complexidade tributária',
      message: 'Gerenciar tantas prefeituras manualmente é um risco',
      impact: 'Automação reduz risco de multas em 87%'
    });
  }
  
  // Error frequency insight
  if (errorFreqAnswer === 'daily' || errorFreqAnswer === 'frequent') {
    insights.push({
      type: 'alert',
      title: 'Alta frequência de erros',
      message: 'Erros frequentes indicam necessidade urgente de automação',
      impact: 'Sistemas automatizados reduzem erros em até 95%'
    });
  }
  
  // If no specific insights, add a general one
  if (insights.length === 0) {
    insights.push({
      type: 'opportunity',
      title: 'Oportunidade de otimização',
      message: 'Sua operação pode ser significativamente melhorada',
      impact: 'Automação pode liberar tempo para atividades estratégicas'
    });
  }
  
  return {
    score: finalScore,
    category,
    categoryClass,
    categoryDescription,
    currentState: {
      hoursPerDay: `${dailyHours}h/dia`,
      hoursPerWeek: weeklyHours,
      errorFrequency: errorFreqText,
      monthlyCost: `R$ ${monthlyCost.toLocaleString('pt-BR')}`
    },
    comparison: {
      manual: {
        hours: weeklyHours,
        documents: weeklyHours * 20, // Assuming 20 docs/hour
        errorRate: Math.min(15, Math.round(finalScore * 0.15))
      },
      automated: {
        hours: automatedHours,
        documents: 'Ilimitados',
        errorRate: 0.5 // 95% reduction from average error rate
      }
    },
    savings: {
      weeklyHours: timeSavings,
      monthlyAmount: `R$ ${monthlySavings.toLocaleString('pt-BR')}`,
      annualAmount: `R$ ${annualSavings.toLocaleString('pt-BR')}`,
      roiPeriod: `${roiMonths} meses`
    },
    insights
  };
};